/**
 * Barcode scanner for the header search.
 *
 * A button in the search field opens the camera, reads the product barcode
 * (EAN/UPC/Code 128...) and looks it up through Shopify predictive search on
 * the variant barcode field and product tags (extra barcodes of the same
 * product are stored as tags). One match opens the product directly, several
 * matches open the search page, no match shows a retry message.
 *
 * Decoding uses the native BarcodeDetector where available (Chrome Android)
 * and falls back to the zxing-wasm based ponyfill (iOS Safari), which is
 * loaded only when the scanner is opened.
 */
(function () {
  'use strict';

  const PONYFILL_URL = 'https://cdn.jsdelivr.net/npm/barcode-detector@3.2.2/dist/iife/ponyfill.js';
  const FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];
  const SCAN_INTERVAL = 150;

  const selectors = {
    openButton: '[data-barcode-scanner-open]',
  };

  const supportsCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) && window.isSecureContext;

  let detectorPromise = null;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load ' + src));
      document.head.appendChild(script);
    });
  }

  function getDetector() {
    if (detectorPromise) return detectorPromise;

    detectorPromise = (async () => {
      if ('BarcodeDetector' in window) {
        try {
          const supported = await window.BarcodeDetector.getSupportedFormats();
          const formats = FORMATS.filter((format) => supported.includes(format));
          if (formats.length) return new window.BarcodeDetector({formats});
        } catch (error) {
          // Fall through to the ponyfill
        }
      }

      if (!window.BarcodeDetectionAPI) await loadScript(PONYFILL_URL);
      return new window.BarcodeDetectionAPI.BarcodeDetector({formats: FORMATS});
    })();

    detectorPromise.catch(() => {
      detectorPromise = null;
    });

    return detectorPromise;
  }

  // Shopify may store a UPC-A code with or without the leading zero of its EAN-13 form
  function getCodeVariants(code) {
    const variants = [code];
    if (/^0\d{12}$/.test(code)) variants.push(code.slice(1));
    if (/^\d{12}$/.test(code)) variants.push('0' + code);
    return variants;
  }

  async function findProducts(code) {
    const root = window.theme && window.theme.routes ? window.theme.routes.predictive_search_url : '/search/suggest';
    const params = new URLSearchParams({
      q: code,
      'resources[type]': 'product',
      'resources[limit]': '2',
      'resources[options][fields]': 'variants.barcode,tag',
      'resources[options][prefix]': 'none',
      'resources[options][unavailable_products]': 'last',
    });

    const response = await fetch(`${root}.json?${params.toString()}`);
    if (!response.ok) throw new Error(response.status);
    const data = await response.json();
    return (data.resources && data.resources.results && data.resources.results.products) || [];
  }

  function getSearchPageUrl(code) {
    const root = `${(window.theme && window.theme.routes && window.theme.routes.root) || '/'}search`;
    const params = new URLSearchParams({
      q: code,
      type: 'product',
      'options[fields]': 'variants.barcode,tag',
      'options[prefix]': 'none',
    });
    return `${root}?${params.toString()}`;
  }

  class BarcodeScannerModal {
    constructor(strings) {
      this.strings = strings;
      this.stream = null;
      this.timer = null;
      this.isOpen = false;
      this.isBusy = false;
      this.previousFocus = null;

      this.onKeyup = this.onKeyup.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);

      this.render();
    }

    render() {
      this.element = document.createElement('div');
      this.element.className = 'barcode-scanner';
      this.element.setAttribute('role', 'dialog');
      this.element.setAttribute('aria-modal', 'true');
      this.element.setAttribute('aria-label', this.strings.title);
      this.element.hidden = true;
      this.element.innerHTML = `
        <video class="barcode-scanner__video" playsinline muted autoplay></video>
        <div class="barcode-scanner__frame" aria-hidden="true"><span class="barcode-scanner__line"></span></div>
        <button type="button" class="barcode-scanner__close" data-barcode-scanner-close></button>
        <div class="barcode-scanner__panel">
          <p class="barcode-scanner__message" role="status" aria-live="polite"></p>
          <button type="button" class="barcode-scanner__retry btn btn--primary btn--solid" data-barcode-scanner-retry hidden></button>
        </div>
      `;

      this.video = this.element.querySelector('video');
      this.message = this.element.querySelector('.barcode-scanner__message');
      this.closeButton = this.element.querySelector('[data-barcode-scanner-close]');
      this.retryButton = this.element.querySelector('[data-barcode-scanner-retry]');

      this.closeButton.setAttribute('aria-label', this.strings.close);
      this.closeButton.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M6.758 17.243 12.001 12m5.243-5.243L12 12m0 0L6.758 6.757M12.001 12l5.243 5.243" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';
      this.retryButton.textContent = this.strings.retry;

      this.closeButton.addEventListener('click', () => this.close());
      this.retryButton.addEventListener('click', () => this.start());
      // Keep the header search popdown from closing on clicks inside the scanner
      this.element.addEventListener('click', (event) => event.stopPropagation());

      document.body.appendChild(this.element);
    }

    setMessage(text, showRetry = false) {
      this.message.textContent = text;
      this.retryButton.hidden = !showRetry;
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.previousFocus = document.activeElement;
      this.element.hidden = false;
      document.documentElement.classList.add('barcode-scanner-open');
      document.addEventListener('keyup', this.onKeyup);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.closeButton.focus();
      this.start();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.stop();
      this.element.hidden = true;
      document.documentElement.classList.remove('barcode-scanner-open');
      document.removeEventListener('keyup', this.onKeyup);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus();
    }

    async start() {
      this.stop();
      this.isBusy = false;
      this.element.classList.remove('is-paused');
      this.setMessage(this.strings.hint);

      try {
        const [stream, detector] = await Promise.all([
          navigator.mediaDevices.getUserMedia({
            audio: false,
            video: {
              facingMode: {ideal: 'environment'},
              width: {ideal: 1280},
              height: {ideal: 720},
            },
          }),
          getDetector(),
        ]);

        if (!this.isOpen) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        this.stream = stream;
        this.detector = detector;
        this.video.srcObject = stream;
        await this.video.play();
        this.scan();
      } catch (error) {
        const denied = error && (error.name === 'NotAllowedError' || error.name === 'SecurityError');
        this.setMessage(denied ? this.strings.denied : this.strings.error, !denied);
      }
    }

    stop() {
      clearTimeout(this.timer);
      this.timer = null;
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      this.video.srcObject = null;
    }

    scan() {
      if (!this.isOpen || !this.stream || this.isBusy) return;

      this.timer = setTimeout(async () => {
        if (!this.isOpen || !this.stream) return;

        try {
          if (this.video.readyState >= 2) {
            const results = await this.detector.detect(this.video);
            const code = results.length ? String(results[0].rawValue || '').trim() : '';
            if (code) {
              this.onDetected(code);
              return;
            }
          }
        } catch (error) {
          // A single failed frame is not fatal, keep scanning
        }

        this.scan();
      }, SCAN_INTERVAL);
    }

    async onDetected(code) {
      this.isBusy = true;
      this.element.classList.add('is-paused');
      this.video.pause();
      if (navigator.vibrate) navigator.vibrate(80);
      this.setMessage(`${this.strings.searching} ${code}`);

      try {
        let products = [];
        for (const variant of getCodeVariants(code)) {
          products = await findProducts(variant);
          if (products.length) break;
        }

        if (products.length === 1) {
          this.stop();
          window.location.href = products[0].url;
          return;
        }

        if (products.length > 1) {
          this.stop();
          window.location.href = getSearchPageUrl(code);
          return;
        }

        this.stop();
        this.setMessage(`${this.strings.notFound} ${code}`, true);
      } catch (error) {
        this.stop();
        this.setMessage(this.strings.error, true);
      }
    }

    onKeyup(event) {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
    }

    onVisibilityChange() {
      if (document.hidden && this.isOpen) this.close();
    }
  }

  let modal = null;

  function getStrings(button) {
    return {
      title: button.getAttribute('aria-label') || '',
      hint: button.dataset.hint || '',
      close: button.dataset.closeLabel || '',
      searching: button.dataset.searching || '',
      notFound: button.dataset.notFound || '',
      retry: button.dataset.retry || '',
      denied: button.dataset.denied || '',
      error: button.dataset.error || '',
    };
  }

  function showButtons() {
    if (!supportsCamera) return;
    document.querySelectorAll(selectors.openButton).forEach((button) => {
      button.hidden = false;
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest(selectors.openButton);
    if (!button || !supportsCamera) return;

    event.preventDefault();
    if (!modal) modal = new BarcodeScannerModal(getStrings(button));
    modal.open();
  });

  showButtons();
  document.addEventListener('shopify:section:load', showButtons);
})();
